import { FC, useState, useMemo } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { GridColumns, DataGrid, GridRowParams, GridCellParams, GridSortDirection } from '@material-ui/data-grid';
import { Grid, Paper, Tooltip } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { toFormatDateTime } from 'utils';
import Calender from './../../../assets/icons/EndUser/Calender.svg';
import IPIcon from '../../../assets/icons/AdminIcons/IP.svg';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useIsMobile } from 'hooks/useResponsive';
import { handleCompanyUrl } from 'utils/organizationUtils';

export const DevicesTable: FC = observer(() => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const {
    devicesTableStore: { getDevicesInfo, devicesView, getEndUserDevicesInfo },
    logsTableStore: { viewUser, viewEndUser },
    notificationStore: { openNotification },
  } = useStore();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const profile = JSON.parse(localStorage.getItem('Profile') as string);
  const endUserConfigSettings = JSON.parse(localStorage.getItem('endUserConfigSettings') as string);
  const userId = endUserConfigSettings?.userId;
  const getDevicesReq = useQuery(
    ['getDevicesReq', page, pageSize],
    () => {
      if (profile?.permission !== 'EndUser' && profile?.permission !== 'Manager') {
        return getDevicesInfo(page + 1, pageSize);
      } else {
        return getEndUserDevicesInfo(page + 1, pageSize);
      }
    },
    {
      keepPreviousData: true,
      onSuccess: (response) => {
        if (response?.meta?.total) {
          setTotalRows(response.meta.total);
        } else if (response?.total) {
          setTotalRows(response.total);
        } else {
          setTotalRows(response?.logs?.length || 0);
        }
      },
      onError: () => openNotification('error', `Can't get Devices Detail`),
    }
  );
  const row = useMemo(() => {
    return !getDevicesReq.isFetching ? devicesView : [];
  }, [getDevicesReq.isFetching, devicesView]);

  const getClickedUser = useMutation(
    'viewUser',
    (ClientId: number) =>
      profile?.permission !== 'EndUser' && profile?.permission !== 'Manager'
        ? viewUser(ClientId)
        : viewEndUser(ClientId),
    {
      onSuccess: (user) => {
        let url;
        if (user?.data?.message === 'End User Group and Department') {
          url = `${handleCompanyUrl('/admin/profile')}`;
          window.open(url, '_blank');
        }
        localStorage.setItem('ClickedUser', JSON.stringify(user.data.data));
        if (user.data.data.carlicense) {
          url = `${handleCompanyUrl('/admin/view')}`;
          window.open(url, '_blank');
        }
        url = `${handleCompanyUrl('/user/view')}`;
        window.open(url, '_blank');
      },
      onError: (error: any) => {
        openNotification('error', error.data.message);
      },
    }
  );

  const columns: GridColumns = [
    { field: 'id', headerName: 'ID', width: 300, hide: true },
    {
      field: 'login_time',
      headerName: 'DATE AND TIME',
      width: isMobile ? 250 : 280,
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const time = params.row.login_time;
        return (
          <Tooltip
            title={
              <div>
                <span>{time}</span>
              </div>
            }
            placement='top'
          >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <img src={Calender} alt='' style={{ width: '14px', height: '14px' }} />
              <span
                style={{
                  fontSize: isMobile ? '0.7rem' : '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {toFormatDateTime(params.row.login_time.toString())}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'ip_address',
      headerName: 'LOCATION',
      width: isMobile ? 400 : 300,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        const ip = params.row.ip_address;
        const city = params.row.city;
        const country = params.row.country;
        return (
          <>
            <Tooltip
              title={
                <div>
                  <span>{ip}</span>
                  <br />
                  <span>{city}</span>
                  <br />
                  <span>{country}</span>
                  <br />
                </div>
              }
              placement='top'
            >
              <div style={{ display: 'flex', gap: '10px' }}>
                <>
                  {' '}
                  <img src={IPIcon} alt='' />
                  <span>{ip}</span>,<span>{city}</span>,<span>{country}</span>
                </>
              </div>
            </Tooltip>
          </>
        );
      },
    },
    ...(profile?.permission !== 'EndUser' && profile?.permission !== 'Manager'
      ? [
          {
            field: 'userName',
            headerName:
              endUserConfigSettings?.userId === null || endUserConfigSettings?.userId === '' ? 'User Id' : userId,
            width: 200,
            flex: 0.5,
            hide: isMobile,
            renderCell: (params: GridCellParams) => {
              const handleClick = (event: any) => {
                const id = params.row.user.id;
                event.stopPropagation();
                if (id === 1) {
                  const url = `${handleCompanyUrl('/admin/profile')}`;
                  window.open(url, '_blank');
                } else {
                  getClickedUser.mutate(id);
                }
              };

              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Tooltip title={params.row.user.name} placement='top'>
                    <span
                      onClick={handleClick}
                      style={{
                        cursor: 'pointer',
                        color: '#1976d2',
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {params.row.user.name}
                    </span>
                  </Tooltip>
                </div>
              );
            },
          },
        ]
      : []),
    {
      field: 'device',
      headerName: 'DEVICES',
      width: 200,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        const device = params.row.device;
        const browser = params.row.browser;
        const os = params.row.os;

        const displayText = isMobile ? `${device}` : `${device}, ${browser}, ${os}`;

        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tooltip
              title={
                <div>
                  <div>Device: {device}</div>
                  <div>Browser: {browser}</div>
                  <div>OS: {os}</div>
                </div>
              }
              placement='top'
            >
              <span
                style={{
                  fontSize: isMobile ? '0.7rem' : '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayText}
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];
  useAppPreloader(getDevicesReq.isFetching);

  return (
    <>
      {(profile?.permission === 'Manager' || profile?.permission === 'EndUser') && (
        <h1 style={{ fontWeight: '500', fontSize: '20px', textAlign: 'center', margin: '10px' }}>
          Devices and Locations Used For Access
        </h1>
      )}
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <div style={{ display: 'flex', alignItems: 'center' }}>{/* <Search handelSearch={setFilter} /> */}</div>
      </Grid>
      <Paper className={classes.dataGrid} elevation={0}>
        <div
          style={{
            minWidth: isMobile ? 900 : '100%',
            overflowX: 'auto',
          }}
        >
          <DataGrid
            key={totalRows}
            className={classes.noBorder}
            disableSelectionOnClick
            getRowClassName={(params: GridRowParams) => `row-background-${params.getValue('type')}`}
            sortingOrder={['desc', 'asc']}
            sortModel={[
              {
                field: 'id',
                sort: 'desc' as GridSortDirection,
              },
            ]}
            autoHeight
            rows={row}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            paginationMode='server'
            rowCount={totalRows}
            page={page}
            onPageChange={(params) => setPage(params.page)}
            onPageSizeChange={(params) => {
              setPageSize(params.pageSize);
              setPage(0);
            }}
          />
        </div>
      </Paper>
    </>
  );
});
